import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiUser, FiShield, FiCheck, FiChevronLeft, FiChevronRight, FiFileText, FiKey } from 'react-icons/fi';
import { useTokenIssuer, validateIssuerData } from '../../contexts/TokenIssuerContext';
import {
  formatSectorLabel,
  labelTokenizerPlatformCategoryId,
  mergeTokenizerFormCategories
} from '../../api/smartTokenizerMappers';
import { TokenizationSectorsCheckboxGrid } from '../shared/TokenizationSectorsCheckboxGrid';
import {
  WalletInputRow,
  GenerateWalletBtn,
  OneTimeKeyRevealDialog,
  useOneTimeWalletReveal
} from '../shared/oneTimeWalletKeyReveal';
import toast from 'react-hot-toast';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Modal = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a3e 0%, #2d2d5f 100%);
  border-radius: 16px;
  width: 100%;
  max-width: 1000px;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    max-height: 98vh;
    border-radius: 12px;
  }
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  h2 {
    color: #fff;
    font-size: 1.8rem;
    font-weight: 700;
    margin: 0;
  }

  .subtitle {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-top: 0.35rem;
    line-height: 1.4;
    max-width: 640px;
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 8px;
    padding: 0.5rem;
    color: #fff;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .steps {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }

  .step {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.3s ease;
    white-space: nowrap;

    &.active {
      background: rgba(79, 172, 254, 0.2);
      color: #4facfe;
      border: 1px solid rgba(79, 172, 254, 0.3);
    }

    &.completed {
      background: rgba(34, 197, 94, 0.2);
      color: #22c55e;
      border: 1px solid rgba(34, 197, 94, 0.3);
    }

    &.pending {
      background: rgba(255, 255, 255, 0.05);
      color: #a0a0a0;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }

    .step-number {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background: currentColor;
      color: #1a1a3e;
      font-weight: 600;
      font-size: 0.8rem;
    }
  }

  .divider {
    width: 2rem;
    height: 1px;
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;

    .steps {
      gap: 0.5rem;
    }

    .step {
      padding: 0.4rem 0.75rem;
      font-size: 0.8rem;

      .step-number {
        width: 20px;
        height: 20px;
        font-size: 0.7rem;
      }
    }

    .divider {
      width: 1rem;
    }
  }
`;

const Content = styled.div`
  padding: 2rem;
  flex: 1;
  overflow-y: auto;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const StepContent = styled(motion.div)`
  min-height: 260px;
`;

const FormSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    .icon {
      color: #4facfe;
    }
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.columns || '1fr'};
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;

  label {
    color: #fff;
    font-weight: 500;
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }

  input,
  select,
  textarea {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    padding: 0.75rem;
    color: #fff;
    font-size: 0.9rem;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4facfe;
      box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1);
    }

    &::placeholder {
      color: #a0a0a0;
    }
  }

  select {
    option {
      background: #1a1a3e;
      color: #fff;
    }
  }

  textarea {
    min-height: 100px;
    resize: vertical;
  }

  .field-hint {
    font-size: 0.8rem;
    color: #a0a0a0;
    margin-top: 0.25rem;
  }

  .field-error {
    font-size: 0.8rem;
    color: #ff6b6b;
    margin-top: 0.25rem;
  }
`;

const Actions = styled.div`
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem;
    flex-direction: column;
    gap: 1rem;

    > div {
      width: 100%;
      display: flex;
      justify-content: center;
      gap: 0.5rem;
    }
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  &.primary {
    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    color: #fff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(79, 172, 254, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  &.ghost {
    background: transparent;
    color: #a0a0a0;

    &:hover {
      color: #fff;
    }
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.9rem;
  }
`;

const ApiNote = styled.p`
  color: #a0a0a0;
  font-size: 0.85rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
`;

const emptyForm = () => ({
  name: '',
  registrationNumber: '',
  walletAddress: '',
  allowedCategories: []
});

const steps = [
  { id: 1, name: 'Organization', icon: <FiUser /> },
  { id: 2, name: 'Sectors', icon: <FiShield /> },
  { id: 3, name: 'Review', icon: <FiFileText /> }
];

const IssuerCreationWizard = ({ isOpen, onClose, editIssuer = null }) => {
  const { addIssuer, updateIssuer, loading, isAdmin } = useTokenIssuer();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [validationErrors, setValidationErrors] = useState({});

  const updateFormData = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const {
    reveal: oneTimeKeyReveal,
    revealAcknowledged,
    setRevealAcknowledged,
    generateWallet: handleGenerateWallet,
    generating: issuerWalletGenerating,
    dismissKeyReveal,
    cancelKeyGeneration,
    walletFieldEmpty,
    keyRevealOpen
  } = useOneTimeWalletReveal({
    walletAddress: formData.walletAddress,
    setWalletAddress: (v) => updateFormData('walletAddress', v)
  });

  useEffect(() => {
    if (!isOpen) {
      dismissKeyReveal();
      return;
    }

    setCurrentStep(1);
    setValidationErrors({});
    dismissKeyReveal();

    if (editIssuer) {
      setFormData({
        name: editIssuer.name || '',
        registrationNumber: editIssuer.registrationNumber || '',
        walletAddress: editIssuer.walletAddress || '',
        allowedCategories: mergeTokenizerFormCategories(editIssuer)
      });
    } else {
      setFormData(emptyForm());
    }
  }, [isOpen, editIssuer, dismissKeyReveal]);

  const toggleArrayValue = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((item) => item !== value)
        : [...prev[field], value]
    }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateCurrentStep = () => {
    const errors = {};

    if (currentStep === 1) {
      if (!formData.name.trim()) errors.name = 'Organization name is required';
      if (!formData.registrationNumber.trim()) errors.registrationNumber = 'Registration number is required';
      if (!formData.walletAddress.trim()) errors.walletAddress = 'Wallet address is required';
      else if (!/^(0x[a-fA-F0-9]{40}|[a-fA-F0-9]{44})$/.test(formData.walletAddress.trim())) {
        errors.walletAddress = 'Use a Core ICAN (44 hex) or 0x + 40 hex for the same account';
      }
    }

    if (currentStep === 2 && formData.allowedCategories.length === 0) {
      errors.allowedCategories = 'Select at least one sector';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (keyRevealOpen) return;
    if (validateCurrentStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    if (keyRevealOpen) return;
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (keyRevealOpen) return;
    const full = validateIssuerData(formData);
    if (!full.isValid) {
      const message = Object.values(full.errors).join(', ');
      toast.error(message);
      setValidationErrors(full.errors);
      setCurrentStep(1);
      return;
    }

    try {
      setIsSubmitting(true);

      if (editIssuer) {
        const result = await updateIssuer(editIssuer.id, formData);
        if (!result.success) {
          toast.error(result.error || 'Update failed');
          return;
        }
        toast.success('Authorized tokenizer updated.');
      } else {
        const result = await addIssuer(formData);
        if (!result.success) {
          toast.error(result.error || 'Create failed');
          return;
        }
        toast.success('Authorized tokenizer created. Approve it when ready.');
      }

      onClose();
    } catch (error) {
      toast.error(error.message || 'Save failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'pending';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <ApiNote>
              These fields map to <code>POST /api/authorized-tokenizers</code> and{' '}
              <code>PUT /api/authorized-tokenizers/{"{id}"}</code>: organization name, registration number,
              wallet address, and sector list.
            </ApiNote>
            <FormSection>
              <div className="section-title">
                <FiUser className="icon" />
                Organization
              </div>
              <FormGroup>
                <label>Organization name *</label>
                <input
                  type="text"
                  placeholder="Legal entity name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                />
                {validationErrors.name && <div className="field-error">{validationErrors.name}</div>}
              </FormGroup>
              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <label>Registration number *</label>
                  <input
                    type="text"
                    placeholder="Company / registry ID"
                    value={formData.registrationNumber}
                    onChange={(e) => updateFormData('registrationNumber', e.target.value)}
                  />
                  {validationErrors.registrationNumber && (
                    <div className="field-error">{validationErrors.registrationNumber}</div>
                  )}
                </FormGroup>
                <FormGroup>
                  <label>Wallet address *</label>
                  <WalletInputRow>
                    <input
                      type="text"
                      placeholder="44 hex ICAN"
                      value={formData.walletAddress}
                      onChange={(e) => updateFormData('walletAddress', e.target.value)}
                    />
                    {walletFieldEmpty && (
                      <GenerateWalletBtn
                        type="button"
                        onClick={handleGenerateWallet}
                        disabled={issuerWalletGenerating}
                        title="Request Core mnemonic and Xcb.Net extended key from the server (shown once; for Ping.Tokenizer.Signer)"
                      >
                        <FiKey aria-hidden />
                        {issuerWalletGenerating ? 'Generating…' : 'Generate'}
                      </GenerateWalletBtn>
                    )}
                  </WalletInputRow>
                  {validationErrors.walletAddress && (
                    <div className="field-error">{validationErrors.walletAddress}</div>
                  )}
                  {walletFieldEmpty && (
                    <div className="field-hint" style={{ marginTop: '0.35rem' }}>
                      Paste an existing Core ICAN, or use <strong>Generate</strong> to request wallet material. A modal
                      shows the <strong>24-word mnemonic</strong> and <strong>228 hex digits</strong> (114-byte Xcb.Net
                      extended key) for Ping.Tokenizer.Signer once — store them offline before continuing.
                    </div>
                  )}
                </FormGroup>
              </FormRow>
            </FormSection>
          </StepContent>
        );

      case 2:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiShield className="icon" />
                Authorized sectors
              </div>
              <ApiNote>Each selection maps to a backend <code>SectorType</code> for this tokenizer.</ApiNote>
              <TokenizationSectorsCheckboxGrid
                selectedIds={formData.allowedCategories}
                onToggle={(id) => toggleArrayValue('allowedCategories', id)}
                errorMessage={validationErrors.allowedCategories}
              />
            </FormSection>
          </StepContent>
        );

      case 3:
        return (
          <StepContent
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <FormSection>
              <div className="section-title">
                <FiFileText className="icon" />
                Review
              </div>
              <div style={{ color: '#a0a0a0', lineHeight: 1.6 }}>
                <p>
                  <strong style={{ color: '#fff' }}>Organization:</strong> {formData.name}
                </p>
                <p>
                  <strong style={{ color: '#fff' }}>Registration:</strong> {formData.registrationNumber}
                </p>
                <p>
                  <strong style={{ color: '#fff' }}>Wallet:</strong>{' '}
                  <span style={{ fontFamily: 'monospace' }}>{formData.walletAddress}</span>
                </p>
                <p>
                  <strong style={{ color: '#fff' }}>Sectors:</strong>{' '}
                  {formData.allowedCategories.length > 0
                    ? formData.allowedCategories.map((id) => labelTokenizerPlatformCategoryId(id)).join(', ')
                    : (editIssuer?.sectors || []).map(formatSectorLabel).join(', ') || '—'}
                </p>
              </div>
            </FormSection>
          </StepContent>
        );

      default:
        return null;
    }
  };

  if (!isOpen) {
    return null;
  }

  if (!isAdmin) {
    return (
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <Modal
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
        >
          <Header>
            <div>
              <h2>Admin required</h2>
              <div className="subtitle">
                Creating or editing authorized tokenizers requires the Admin role on your account. Sign in as an
                administrator to use this flow.
              </div>
            </div>
            <button type="button" className="close-btn" onClick={onClose}>
              <FiX />
            </button>
          </Header>
          <Actions>
            <div />
            <Button type="button" className="secondary" onClick={onClose}>
              Close
            </Button>
          </Actions>
        </Modal>
      </Overlay>
    );
  }

  return (
    <>
      <OneTimeKeyRevealDialog
        reveal={oneTimeKeyReveal}
        revealAcknowledged={revealAcknowledged}
        onRevealAcknowledgedChange={setRevealAcknowledged}
        onConfirmBackup={dismissKeyReveal}
        onCancelDiscard={cancelKeyGeneration}
      />
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => {
          if (keyRevealOpen) return;
          if (e.target === e.currentTarget) onClose();
        }}
      >
      <Modal
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <Header>
          <div>
            <h2>{editIssuer ? 'Edit authorized tokenizer' : 'Register authorized tokenizer'}</h2>
            <div className="subtitle">
              {editIssuer
                ? 'Updates organization, registration, wallet, and sectors via the backend PUT endpoint.'
                : 'Creates a pending tokenizer. An admin must approve it before it can operate on-chain workflows.'}
            </div>
          </div>
          <button
            type="button"
            className="close-btn"
            onClick={() => {
              if (keyRevealOpen) {
                toast.error('Close the security dialog and confirm you saved the private key first.');
                return;
              }
              onClose();
            }}
          >
            <FiX />
          </button>
        </Header>

        <StepIndicator>
          <div className="steps">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className={`step ${getStepStatus(step.id)}`}>
                  <div className="step-number">
                    {getStepStatus(step.id) === 'completed' ? <FiCheck /> : step.id}
                  </div>
                  <span>{step.name}</span>
                </div>
                {index < steps.length - 1 && <div className="divider" />}
              </React.Fragment>
            ))}
          </div>
        </StepIndicator>

        <Content>
          <AnimatePresence mode="wait">{renderStepContent()}</AnimatePresence>
        </Content>

        <Actions>
          <div>
            {currentStep > 1 && (
              <Button type="button" className="ghost" onClick={handlePrevious} disabled={keyRevealOpen}>
                <FiChevronLeft />
                Previous
              </Button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <Button type="button" className="secondary" onClick={onClose} disabled={keyRevealOpen}>
              Cancel
            </Button>

            {currentStep < steps.length ? (
              <Button type="button" className="primary" onClick={handleNext} disabled={keyRevealOpen}>
                Next
                <FiChevronRight />
              </Button>
            ) : (
              <Button
                type="button"
                className="primary"
                onClick={handleSubmit}
                disabled={isSubmitting || loading || keyRevealOpen}
              >
                {isSubmitting || loading ? 'Saving…' : editIssuer ? 'Save changes' : 'Create tokenizer'}
                <FiCheck />
              </Button>
            )}
          </div>
        </Actions>
      </Modal>
    </Overlay>
    </>
  );
};

export default IssuerCreationWizard;
